// Public SEO article renderer. Register at /blog/:slug.
// Fetches from /api/marketing/articles/:slug, renders body + FAQ + CTA, and
// injects Article + FAQPage JSON-LD into <head>. Self-contained styling (no
// extra CSS files) via a scoped <style> block.
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import axios from "axios";
import { API } from "../apiConfig";

const ARTICLES_BASE = `${API}/marketing`;

function faqJsonLd(faqs) {
  if (!Array.isArray(faqs) || faqs.length === 0) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };
}
function articleJsonLd(page) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: page.title,
    description: page.meta_description,
    datePublished: page.published_at,
    author: { "@type": "Organization", name: "Oly Payroll" },
    publisher: { "@type": "Organization", name: "Oly Payroll" },
    mainEntityOfPage: { "@type": "WebPage", "@id": page.canonical_url },
  };
}

const ARTICLE_CSS = `
.mkt-article{font-size:1.075rem;line-height:1.8}
.mkt-article h1{font-size:2rem;font-weight:800;margin:2rem 0 1rem}
.mkt-article h2{font-size:1.6rem;font-weight:700;margin:2.25rem 0 .9rem}
.mkt-article h3{font-size:1.25rem;font-weight:700;margin:1.6rem 0 .6rem}
.mkt-article p{margin:0 0 1.15rem}
.mkt-article ul,.mkt-article ol{margin:0 0 1.2rem;padding-left:1.4rem}
.mkt-article li{margin-bottom:.5rem;list-style:disc}
.mkt-article ol li{list-style:decimal}
.mkt-article a{color:hsl(var(--primary));text-decoration:underline;text-underline-offset:2px}
.mkt-article strong{font-weight:700}
`;

export default function BlogArticle() {
  const { slug } = useParams();
  const [page, setPage] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    let active = true;
    axios.get(`${ARTICLES_BASE}/articles/${slug}`)
      .then((r) => active && setPage(r.data))
      .catch(() => active && setPage(null));
    return () => { active = false; };
  }, [slug]);

  useEffect(() => {
    if (!page) return;
    document.title = `${page.title} - Oly Payroll`;
    const ld = [articleJsonLd(page), faqJsonLd(page.faq)].filter(Boolean);
    const tags = ld.map((obj) => {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.text = JSON.stringify(obj);
      s.setAttribute("data-blog-ld", "true");
      document.head.appendChild(s);
      return s;
    });
    return () => tags.forEach((t) => t.remove());
  }, [page]);

  if (page === undefined) {
    return <main className="min-h-screen bg-background flex items-center justify-center text-muted-foreground" data-testid="blog-loading">Loading...</main>;
  }
  if (page === null) {
    return (
      <main className="min-h-screen bg-background flex flex-col items-center justify-center gap-3" data-testid="blog-not-found">
        <div className="text-xl font-semibold">Article not found</div>
        <Link to="/" className="text-primary hover:underline">Back home</Link>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-background text-foreground" data-testid="blog-page">
      <style>{ARTICLE_CSS}</style>
      <header className="border-b border-border">
        <div className="container mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="font-bold text-lg">Oly Payroll</Link>
          <Link to="/" className="text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Home
          </Link>
        </div>
      </header>

      <article className="container mx-auto px-5 max-w-3xl py-12">
        <header className="mb-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
            {(page.angle || "").replace(/-/g, " ")} - for {page.audience}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight tracking-tight" data-testid="blog-title">{page.title}</h1>
          {page.meta_description && <p className="mt-4 text-lg text-muted-foreground">{page.meta_description}</p>}
        </header>

        <div className="mkt-article" data-testid="blog-body" dangerouslySetInnerHTML={{ __html: page.body_html || "" }} />

        {Array.isArray(page.faq) && page.faq.length > 0 && (
          <section className="mt-12 border-t border-border pt-8" data-testid="blog-faq">
            <h2 className="text-2xl font-bold mb-6">Frequently asked questions</h2>
            <div className="space-y-6">
              {page.faq.map((f, i) => (
                <div key={i}><div className="font-semibold mb-1">{f.q}</div><div className="text-muted-foreground">{f.a}</div></div>
              ))}
            </div>
          </section>
        )}

        {Array.isArray(page.internal_links) && page.internal_links.length > 0 && (
          <section className="mt-12 border-t border-border pt-8">
            <h2 className="text-2xl font-bold mb-4">Keep reading</h2>
            <ul className="list-disc pl-6 space-y-2">
              {page.internal_links.map((l, i) => (
                <li key={i}><Link to={`/blog/${l.slug}`} className="text-primary hover:underline">{l.title}</Link></li>
              ))}
            </ul>
          </section>
        )}

        <section className="mt-12 p-8 rounded-xl bg-primary text-primary-foreground text-center" data-testid="blog-cta">
          <div className="text-2xl font-bold mb-2">Payroll doesn't have to be painful.</div>
          <p className="opacity-90 mb-5 max-w-xl mx-auto">Oly Payroll — simple, transparent payroll for US small businesses. No surprise fees, fast onboarding, US support.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-background text-foreground font-semibold px-6 py-3 rounded-md hover:opacity-90">
            Get started <ArrowRight className="w-4 h-4" />
          </Link>
        </section>
      </article>
    </main>
  );
}
